import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  IconButton,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';

dayjs.extend(utc);
dayjs.extend(timezone);


const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const MonthlyAttritionDashboard = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [monthlyHistory, setMonthlyHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [calculateDialogOpen, setCalculateDialogOpen] = useState(false);
  const [quarterlyMetricsDialogOpen, setQuarterlyMetricsDialogOpen] = useState(false);
  const [selectedEmployeeForMetrics, setSelectedEmployeeForMetrics] = useState(null);
  const [employeeQuarterlyMetrics, setEmployeeQuarterlyMetrics] = useState([]);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  
  // Separate state for each tab
  const [employeeTabState, setEmployeeTabState] = useState({
    selectedDate: new Date(),
    timeRange: 'currentYear',
    dataExists: false
  });
  
  const [allEmployeesTabState, setAllEmployeesTabState] = useState({
    selectedDate: new Date(),
    timeRange: 'currentYear',
    dataExists: false
  });
  
  const [calculating, setCalculating] = useState(false);
  
  // Filter state for column visibility
  const [filters, setFilters] = useState({
    showAge: false,
    showExperience: false,
    showPerformance: true,
    showEngagement: true,
    showmanagerSatisfactionScore:false,
    showDistance: false,
    showShiftType: false,
    showMobility: false,  
    showPromotions: false,
    showVariablePay: false,
    showJobRole:false,
    showCompensation: true,
    showDepartment: false,
    showLocation: false,
    showEducation: false,
    showTenure: true,
    showTotalExperience: true,
    showWorkLifeBalance: false,
    showAbsenteeism: false,
    showCurrentCTC: false,
    showMarketCTC: false,
    showSalaryHike: false,
    showTeamAttrition: false
  });
  
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [companyConfig, setCompanyConfig] = useState({ currency: '₹', timezone: 'Asia/Kolkata' });

  // Helper function to get latest quarter data for each employee
  const getLatestQuarterData = (employees) => {
    const employeeMap = {};
    
    employees.forEach(employee => {
      const employeeId = employee.employee_id;
      
      if (!employeeMap[employeeId]) {
        employeeMap[employeeId] = employee;
      } else {
        // Compare dates to find the most recent
        const currentDate = new Date(employee.year, employee.month - 1);
        const existingDate = new Date(employeeMap[employeeId].year, employeeMap[employeeId].month - 1);
        
        if (currentDate > existingDate) {
          employeeMap[employeeId] = employee;
        }
      }
    });
    
    // Convert to array and add quarter info
    return Object.values(employeeMap).map(emp => {
      const month = emp.month;
      let quarter = 'Q1';
      if (month >= 4 && month <= 6) quarter = 'Q2';
      else if (month >= 7 && month <= 9) quarter = 'Q3';
      else if (month >= 10 && month <= 12) quarter = 'Q4';
      
      return {
        ...emp,
        quarter: quarter,
        quarterYear: `${quarter} ${emp.year}`,
        quarterKey: `${emp.year}-${quarter}`
      };
    });
  };

  useEffect(() => {
    fetchEmployeesWithMonthlyData();
  }, []);

  const fetchEmployeesWithMonthlyData = async (month = null, year = null) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      let url = `${API_URL}/api/attrition/employees/monthly`;
      
      if (month && year) {
        url += `?month=${month}&year=${year}`;
      }

      const response = await fetch(url,{
        headers:{
          Authorization: `Bearer ${token}`  
        }
      });
      const data = await response.json();

      try {
        const cmpRes = await fetch(`${API_URL}/api/cmp/${company_id}`, { headers: { Authorization: `Bearer ${token}` } });
        const cmpData = await cmpRes.json();
        if (cmpData && cmpData.data) {
          setCompanyConfig({
            currency: cmpData.data.currency || '₹',
            timezone: cmpData.data.timezone || 'Asia/Kolkata'
          });
        }
      } catch(e) {}

      if (data.success) {
        // Get unique employees with latest quarter data
        const uniqueEmployees = getLatestQuarterData(data.data.employees);
        setEmployees(uniqueEmployees);
        
        if (uniqueEmployees.length > 0 && !selectedEmployee) {
          setSelectedEmployee(uniqueEmployees[0]);
          fetchEmployeeMonthlyHistory(uniqueEmployees[0].employee_id);
        }
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to fetch employees data');
      //console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchEmployeeMonthlyHistory = async (employeeId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const response = await fetch(
        `${API_URL}/api/attrition/employee/${employeeId}/monthly?months=24`,{
          headers: {
            Authorization: `Bearer ${token}`
        }
      }
      );
      const data = await response.json();
      if (data.success) {
        setMonthlyHistory(data.data.monthlyHistory);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to fetch employee history');
      //console.error('Error:', err);
    }
  };

  const checkDataExists = async (month, year) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const response = await fetch(
        `${API_URL}/api/attrition/monthly/check?month=${month}&year=${year}`,{
          headers:{
            Authorization: `Bearer ${token}`
          }
        }
      );
      const data = await response.json();
      
      if (data.success) {
        // Update the appropriate tab's state
        if (tabValue === 0) {
          setEmployeeTabState(prev => ({ ...prev, dataExists: data.data.exists }));
          if (data.data.exists) {
            setError(`Data already exists for ${month}/${year}. Please choose a different period.`);
          }
        } else {
          setAllEmployeesTabState(prev => ({ ...prev, dataExists: data.data.exists }));
          if (data.data.exists) {
            setError(`Data already exists for ${month}/${year}. Please choose a different period.`);
          }
        }
      }
    } catch (err) {
      //console.error('Error checking data:', err);
    }
  };

  const calculateMonthlyAttrition = async () => {
    try {
      setCalculating(true);
      
      // Get date from the appropriate tab
      const selectedDate = tabValue === 0 ? employeeTabState.selectedDate : allEmployeesTabState.selectedDate;
      const month = selectedDate.getMonth() + 1;
      const year = selectedDate.getFullYear();
      
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await fetch(`${API_URL}/api/attrition/monthly`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ month, year })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Successfully calculated attrition risk for ${data.data.processed} employees for ${month}/${year}`);
        setAlert({
          open: true,
          message: `✨ Successfully calculated attrition risk for ${data.data.processed} employees for ${month}/${year}!`,
          severity: 'success'
        });
        setCalculateDialogOpen(false);
        fetchEmployeesWithMonthlyData(month, year);
        
        // Reset dataExists flag for both tabs
        setEmployeeTabState(prev => ({ ...prev, dataExists: false }));
        setAllEmployeesTabState(prev => ({ ...prev, dataExists: false }));
      } else {
        if (data.code === "AI_PLAN_UPGRADE_REQUIRED") {
          setAlert({
            open: true,
            message: data.message || "AI features are not included in your current plan. Please upgrade to unlock AI.",
            severity: "warning"
          });
          window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI Attrition Analytics" } }));
        } else if (data.code === "AI_TOKENS_EXHAUSTED") {
          setAlert({
            open: true,
            message: `⚠️ ${data.message || "You have used all your AI tokens for this billing cycle."}`,
            severity: "warning"
          });
        } else {
          setAlert({
            open: true,
            message: data.message || 'Failed to calculate monthly attrition',
            severity: 'error'
          });
        }
        setError(data.message);
      }
    } catch (err) {
      setAlert({
        open: true,
        message: 'Failed to calculate monthly attrition',
        severity: 'error'
      });
      setError('Failed to calculate monthly attrition');
      //console.error('Error:', err);
    } finally {
      setCalculating(false);
    }
  };

  const handleEmployeeChange = (event) => {
    const employeeId = event.target.value;
    const employee = employees.find(emp => emp.employee_id === employeeId);
    setSelectedEmployee(employee);
    fetchEmployeeMonthlyHistory(employeeId);
  };

  const handleViewQuarterlyMetrics = async (employee) => {
    try {
      setLoadingMetrics(true);
      setSelectedEmployeeForMetrics(employee);
      
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await fetch(
        `${API_URL}/api/attrition/employee/${employee.employee_id}/monthly?months=24`,{
          headers:{
            Authorization: `Bearer ${token}`
          }
        }
      );
      const data = await response.json();
      if (data.success) {
        const monthlyHistory = data.data.monthlyHistory;
        const currentYear = new Date().getFullYear();
        
        // Group by quarters
        const quarters = [
          { id: 'Q1', label: 'Q1', months: [1, 2, 3] },
          { id: 'Q2', label: 'Q2', months: [4, 5, 6] },
          { id: 'Q3', label: 'Q3', months: [7, 8, 9] },
          { id: 'Q4', label: 'Q4', months: [10, 11, 12] }
        ];
        
        const quarterlyMetrics = [];
        
        quarters.forEach(q => {
          const quarterRecords = monthlyHistory.filter(record => 
            q.months.includes(record.month) && record.year === currentYear
          );
          
          if (quarterRecords.length > 0) {
            const latestRecord = quarterRecords[quarterRecords.length - 1];
            const avgRiskScore = quarterRecords.reduce((sum, record) => 
              sum + record.riskScore, 0) / quarterRecords.length;
            
            quarterlyMetrics.push({
              quarter: q.label,
              year: currentYear,
              hasData: true,
              risk_score: Math.round(avgRiskScore),
              risk_level: latestRecord.riskLevel,
              performance_trend: latestRecord.performanceTrend,
              engagement_score: latestRecord.engagementScore,
              work_life_balance_score: latestRecord.workLifeBalanceScore,
              compensation_gap_percentage: latestRecord.compensationGapPercentage || latestRecord.market_compensation_gap,
              unplanned_leave_percentage: latestRecord.unplannedLeavePercentage,
              absenteeism_rate: latestRecord.absenteeismRate,
              current_ctc: latestRecord.currentCTC,
              market_ctc: latestRecord.marketCTC,
              salary_hike: latestRecord.salaryHike,
              tenure: latestRecord.tenure
            });
          } else {
            quarterlyMetrics.push({
              quarter: q.label,
              year: currentYear,
              hasData: false,
              risk_score: 0,
              risk_level: 'No Data',
              performance_trend: 'No Data',
              engagement_score: 0,
              work_life_balance_score: 0,
              compensation_gap_percentage: 0,
              unplanned_leave_percentage: 0,
              absenteeism_rate: 0,
              current_ctc: 0,
              market_ctc: 0,
              salary_hike: 0,
              tenure: 0
            });
          }
        });
        
        setEmployeeQuarterlyMetrics(quarterlyMetrics);
      } else {
        // If no history, create empty quarterly metrics
        const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
        const currentYear = new Date().getFullYear();
        const emptyMetrics = quarters.map(q => ({
          quarter: q,
          year: currentYear,
          hasData: false,
          risk_score: 0,
          risk_level: 'No Data',
          performance_trend: 'No Data',
          engagement_score: 0,
          work_life_balance_score: 0,
          compensation_gap_percentage: 0,
          unplanned_leave_percentage: 0,
          absenteeism_rate: 0,
          current_ctc: 0,
          market_ctc: 0,
          salary_hike: 0,
          tenure: 0
        }));
        setEmployeeQuarterlyMetrics(emptyMetrics);
      }
      
      setQuarterlyMetricsDialogOpen(true);
    } catch (err) {
      setError('Failed to fetch employee quarterly metrics');
      //console.error('Error:', err);
    } finally {
      setLoadingMetrics(false);
    }
  };

  const handleDateChange = (date, tab) => {
    if (tab === 0) {
      setEmployeeTabState(prev => ({ ...prev, selectedDate: date }));
    } else {
      setAllEmployeesTabState(prev => ({ ...prev, selectedDate: date }));
    }
    
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    checkDataExists(month, year);
  };

  const handleTimeRangeChange = (value, tab) => {
    if (tab === 0) {
      setEmployeeTabState(prev => ({ ...prev, timeRange: value }));
    } else {
      setAllEmployeesTabState(prev => ({ ...prev, timeRange: value }));
    }
  };

  const handleCalculateClick = () => {
    const selectedDate = tabValue === 0 ? employeeTabState.selectedDate : allEmployeesTabState.selectedDate;
    const month = selectedDate.getMonth() + 1;
    const year = selectedDate.getFullYear();
    checkDataExists(month, year);
    setCalculateDialogOpen(true);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const riskColors = {
    High: '#f44336',
    Medium: '#ff9800',
    Low: '#4caf50'
  };

  const performanceTrendColors = {
    stable: '#4caf50',
    improving: '#2196f3',
    declining: '#f44336'
  };

  const PIE_COLORS = ['#f44336', '#ff9800', '#4caf50'];

  // Risk distribution data for pie chart
  const riskDistributionData = [
    { name: 'High Risk', value: employees.filter(emp => emp.risk_level === 'High').length },
    { name: 'Medium Risk', value: employees.filter(emp => emp.risk_level === 'Medium').length },
    { name: 'Low Risk', value: employees.filter(emp => emp.risk_level === 'Low').length },
  ];

  // Get current quarter metrics for selected employee
  const getCurrentQuarterMetrics = useMemo(() => {
    if (!selectedEmployee) return null;
    
    // Find the latest quarter data for the selected employee
    const latestRecord = employees.find(emp => 
      emp.employee_id === selectedEmployee.employee_id
    );
    if (!latestRecord) return null;
    
    return {
      quarter: latestRecord.quarterYear,
      risk_score: latestRecord.risk_score,
      risk_level: latestRecord.risk_level,
    distance_km: latestRecord.distanceKm,
      shiftType: latestRecord.shiftType,   
educationLevel: latestRecord.educationLevel,
      performance_trend: latestRecord.performance_trend,
      engagement_score: latestRecord.engagement_score,
      compensation_gap_percentage: latestRecord.compensationGapPercentage || 0,
      work_life_balance_score: latestRecord.workLifeBalanceScore || 0,
      absenteeism_rate: latestRecord.absenteeismRate || 0,
      current_ctc: latestRecord.currentCTC || 0,
      market_ctc: latestRecord.marketCTC || 0,
      salary_hike: latestRecord.salary_hike || 0,
      tenureinpresent: latestRecord.tenureinpresent || 0,
      tenure: latestRecord.tenure || 0,
      mobility:latestRecord.internalMobility || false,
      lastPromotionMonths: latestRecord.promotion_last_24_months || false,
      variablepay:latestRecord.variablepay || 0,
      age: latestRecord.age || "N/A",
      JobRole: latestRecord.designation || "N/A",

    };
  }, [selectedEmployee, employees]);



const getQuarterlyChartData = useMemo(() => {
  if (!selectedEmployee || monthlyHistory.length === 0) return [];

  const currentYear = new Date().getFullYear();
  const quarters = [
    { id: 'Q1', label: 'Q1', months: [1, 2, 3] },
    { id: 'Q2', label: 'Q2', months: [4, 5, 6] },
    { id: 'Q3', label: 'Q3', months: [7, 8, 9] },
    { id: 'Q4', label: 'Q4', months: [10, 11, 12] }
  ];

  // Step 1: Find which quarters have data
  const data = quarters.map(q => {
    const quarterRecords = monthlyHistory.filter(record => 
      q.months.includes(record.month) && record.year === currentYear
    );
    
    const hasData = quarterRecords.length > 0;
    
    return {
      quarter: `${q.label} ${currentYear}`,
      label: q.label,
      year: currentYear,
      hasData,
      riskScore: hasData ? 
        Math.round(quarterRecords.reduce((sum, record) => sum + record.riskScore, 0) / quarterRecords.length) : 
        0,
      riskLevel: hasData ? quarterRecords[quarterRecords.length - 1].riskLevel : 'No Data',
      fullDate: `${q.label} ${currentYear}`
    };
  });

  // Step 2: Find the last quarter with data
  let lastDataIndex = -1;
  for (let i = data.length - 1; i >= 0; i--) {
    if (data[i].hasData) {
      lastDataIndex = i;
      break;
    }
  }

  // Step 3: If no data at all, return empty
  if (lastDataIndex === -1) {
    return [];
  }

  // Step 4: Process data up to lastDataIndex
  const result = [];
  let currentValue = 0;
  
  for (let i = 0; i <= lastDataIndex; i++) {
    if (data[i].hasData) {
      currentValue = data[i].riskScore;
      result.push({
        ...data[i],
        riskScore: currentValue
      });
    } else {
      // No data in this quarter
      // Check if there's future data (beyond current i but within lastDataIndex)
      let hasFutureData = false;
      for (let j = i + 1; j <= lastDataIndex; j++) {
        if (data[j].hasData) {
          hasFutureData = true;
          break;
        }
      }
      
      if (hasFutureData) {
        // There's data in a future quarter, so show 0%
        result.push({
          ...data[i],
          riskScore: 0
        });
      } else {
        // No future data, but we're still before lastDataIndex
        // This shouldn't happen if lastDataIndex is correct, but handle it
        result.push({
          ...data[i],
          riskScore: currentValue
        });
      }
    }
  }

  return result;
}, [selectedEmployee, monthlyHistory]);
  const chartData = getQuarterlyChartData;

  // Filter Dialog Component
  const FilterDialog = () => (
    <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)}>
      <DialogTitle>Select Columns to Display</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showAge}
                  onChange={(e) => setFilters({...filters, showAge: e.target.checked})}
                />
              }
              label="Age"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showPerformance}
                  onChange={(e) => setFilters({...filters, showPerformance: e.target.checked})}
                />
              }
              label="Performance Trend"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showEngagement}
                  onChange={(e) => setFilters({...filters, showEngagement: e.target.checked})}
                />
              }
              label="Engagement Score"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showEngagement}
                  onChange={(e) => setFilters({...filters, showEngagement: e.target.checked})}
                />
              }
              label="Manager Satisfaction Score"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showCompensation}
                  onChange={(e) => setFilters({...filters, showCompensation: e.target.checked})}
                />
              }
              label="Compensation Gap"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showDepartment}
                  onChange={(e) => setFilters({...filters, showDepartment: e.target.checked})}
                />
              }
              label="Department"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showEducation}
                  onChange={(e) => setFilters({...filters, showEducation: e.target.checked})}
                />
              }
              label="Education"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showTenure}
                  onChange={(e) => setFilters({...filters, showTenure: e.target.checked})}
                />
              }
              label="Tenure (Current)"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showTotalExperience}
                  onChange={(e) => setFilters({...filters, showTotalExperience: e.target.checked})}
                />
              }
              label="Total Experience"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showWorkLifeBalance}
                  onChange={(e) => setFilters({...filters, showWorkLifeBalance: e.target.checked})}
                />
              }
              label="Work-Life Balance"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showAbsenteeism}
                  onChange={(e) => setFilters({...filters, showAbsenteeism: e.target.checked})}
                />
              }
              label="Absenteeism Rate"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showCurrentCTC}
                  onChange={(e) => setFilters({...filters, showCurrentCTC: e.target.checked})}
                />
              }
              label="Current CTC"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showMarketCTC}
                  onChange={(e) => setFilters({...filters, showMarketCTC: e.target.checked})}
                />
              }
              label="Market CTC"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showSalaryHike}
                  onChange={(e) => setFilters({...filters, showSalaryHike: e.target.checked})}
                />
              }
              label="Salary Hike %"
            />
          </Grid>
           <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showShiftType}
                  onChange={(e) => setFilters({...filters, showShiftType: e.target.checked})}
                />
              }
              label="Shift Type"
            />
          </Grid>
           <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showMobility}
                  onChange={(e) => setFilters({...filters, showMobility: e.target.checked})}
                />
              }
              label="Internal Mobility History"
            />
          </Grid>
           <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showPromotions}
                  onChange={(e) => setFilters({...filters, showPromotions: e.target.checked})}
                />
              }
              label="Last Promotion (24 months)"
            />
          </Grid>
           <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showDistance}
                  onChange={(e) => setFilters({...filters, showDistance: e.target.checked})}
                />
              }
              label="Distance from Home (km)"
            />
          </Grid>
           <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showVariablePay}
                  onChange={(e) => setFilters({...filters, showVariablePay: e.target.checked})}
                />
              }
              label="Variable Pay vs Expected Pay"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showJobRole}
                  onChange={(e) => setFilters({...filters, showJobRole: e.target.checked})}
                />
              }
              label="Job Role"
            />
          </Grid>
          
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
        <Button onClick={() => setFilterDialogOpen(false)} variant="contained">Apply Filters</Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Attrition
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Track and analyze employee attrition risk quarter-over-quarter
          </Typography>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

               <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 3 }}
        >
          <Tab label="Employee Risk Analysis" />
          <Tab label="All Employees Risk" />
        </Tabs>


        {/* Control Panel for Employee Risk Analysis Tab */}
        {tabValue === 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Select Employee</InputLabel>
                    <Select
                      value={selectedEmployee?.employee_id || ''}
                      label="Select Employee"
                      onChange={handleEmployeeChange}
                    >
                      {employees.map((employee) => (
                        <MenuItem key={employee.employee_id} value={employee.employee_id}>
                          {employee.name} - {employee.designation} 
                          ({employee.risk_level} Risk, Latest: {employee.quarterYear})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    views={['month', 'year']}
                    label="Select Month & Year"
                    value={dayjs(employeeTabState.selectedDate)}
                    onChange={(date) => handleDateChange(date, 0)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    
                    onClick={handleCalculateClick}
                    disabled={employeeTabState.dataExists}
                    sx={{ height: '56px' }}
                  >
                    Calculate
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Control Panel for All Employees Risk Tab */}
        {tabValue === 1 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={3}>
                  <DatePicker
                    views={['month', 'year']}
                    label="Select Month & Year"
                    value={dayjs(allEmployeesTabState.selectedDate)}
                    onChange={(date) => handleDateChange(date, 1)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setFilterDialogOpen(true)}
                    sx={{ height: '56px', width: '250px' }}
                    startIcon={<FilterListIcon />}
                  >
                    Select Columns
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCalculateClick}
                    disabled={allEmployeesTabState.dataExists}
                    sx={{ height: '56px', width: '300px' }}
                  >
                    Calculate Monthly Attrition
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Tab Content */}
        {tabValue === 0 && selectedEmployee && (
          <Grid container spacing={3}>
            {/* Left Column - Risk Score Chart and Current Quarter Metrics */}
            <Grid item xs={12} md={8}>
              {/* Risk Score Chart */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {selectedEmployee?.name} - Quarterly Risk Score Trend
                    {chartData.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (Data available up to {chartData[chartData.length - 1]?.quarter})
                      </Typography>
                    )}
                  </Typography>
<ResponsiveContainer width="100%" height={300}>
  {chartData.length > 0 ? (
    <LineChart 
      data={chartData}
      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
    >
      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
      <XAxis 
        dataKey="label" 
        tick={{ fill: '#0c0808ff' }}
        axisLine={{ stroke: '#161111ff' }}
        domain={['Q1', 'Q2', 'Q3', 'Q4']}
        type="category"
      />
      <YAxis 
        domain={[0, 100]} 
        tick={{ fill: '#0c0808ff' }}
        axisLine={{ stroke: '#161111ff' }}
        tickCount={6}
        ticks={[0, 20, 40, 60, 80, 100]}
      />
      <Tooltip 
        formatter={(value, name) => {
          if (name === 'riskScore') {
            if (value === 0 && !chartData.find(d => d.label === 'Q1')?.hasData) {
              return ['No Data', 'Risk Score'];
            }
            return [`${value}%`, 'Risk Score'];
          }
          return [value];
        }}
        labelFormatter={(label, payload) => {
          if (payload && payload[0]) {
            return `Quarter: ${payload[0].payload.fullDate || payload[0].payload.quarter}`;
          }
          return `Quarter: ${label}`;
        }}
      />
    <Line 
  type="monotone"
  dataKey="riskScore" 
  stroke="#f44336" 
  strokeWidth={3}
  strokeDasharray="5 5"
  connectNulls={false}  // Changed to false to break the line
  dot={{ 
    fill: (data) => {
      if (!data.hasData && data.riskScore === 0) {
        return 'transparent'; // Hide dots for 0% no-data points
      }
      const colorMap = {
        'High': '#f44336',
        'Medium': '#ff9800',
        'Low': '#4caf50'
      };
      return colorMap[data.riskLevel] || '#f44336';
    },
    stroke: "#fff", 
    strokeWidth: 2, 
    r: 6 
  }}
  activeDot={{ 
    r: 10, 
    fill: (data) => {
      const colorMap = {
        'High': '#f44336',
        'Medium': '#ff9800',
        'Low': '#4caf50'
      };
      return colorMap[data.payload.riskLevel] || '#f44336';
    },
    stroke: "#fff",
    strokeWidth: 3
  }}
  name="Risk Score"
/>
    </LineChart>
  ) : (
    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
      <Typography variant="body1" color="text.secondary">
        No quarterly data available for this employee
      </Typography>
    </Box>
  )}
</ResponsiveContainer>
                </CardContent>
              </Card>

            
            </Grid>

            {/* Right Column - Employee Details */}
            <Grid item xs={12} md={4}>
              <Card >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Employee Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Name
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedEmployee.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Employee ID
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedEmployee.employee_id}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Designation
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedEmployee.designation}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Department
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedEmployee.department}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Current Risk Level
                      </Typography>
                      <Chip 
                        label={selectedEmployee.risk_level}
                        color={
                          selectedEmployee.risk_level === 'High' ? 'error' :
                          selectedEmployee.risk_level === 'Medium' ? 'warning' : 'success'
                        }
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Current Risk Score
                      </Typography>
                      <Typography variant="body1" fontWeight="medium" sx={{ color: riskColors[selectedEmployee.risk_level] }}>
                        {selectedEmployee.risk_score}%
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Latest Quarter
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedEmployee.quarterYear}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Performance Trend
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedEmployee.performance_trend || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewQuarterlyMetrics(selectedEmployee)}
                        // sx={{ mt: 2 }}
                      >
                        View Detailed Quarterly Metrics
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
                          {/* Current Quarter Metrics */}

              {getCurrentQuarterMetrics && (
                <Card sx={{width: '100%'}}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Current Attrition Risk Quarter Metrics ({getCurrentQuarterMetrics.quarter})
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Risk Score
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CircularProgress 
                              variant="determinate" 
                              value={getCurrentQuarterMetrics.risk_score} 
                              size={40}
                              sx={{ 
                                color: riskColors[getCurrentQuarterMetrics.risk_level],
                                mr: 2
                              }}
                            />
                            <Box>
                              <Typography variant="h6">
                                {getCurrentQuarterMetrics.risk_score}%
                              </Typography>
                              <Chip 
                                label={getCurrentQuarterMetrics.risk_level}
                                size="small"
                                color={
                                  getCurrentQuarterMetrics.risk_level === 'High' ? 'error' :
                                  getCurrentQuarterMetrics.risk_level === 'Medium' ? 'warning' : 'success'
                                }
                              />
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Performance Trend
                          </Typography>
                          <Chip 
                            label={getCurrentQuarterMetrics.performance_trend || 'No Data'}
                            sx={{ 
                              backgroundColor: performanceTrendColors[getCurrentQuarterMetrics.performance_trend?.toLowerCase()] + '20',
                              color: performanceTrendColors[getCurrentQuarterMetrics.performance_trend?.toLowerCase()],
                              border: `1px solid ${performanceTrendColors[getCurrentQuarterMetrics.performance_trend?.toLowerCase()]}`,
                              fontSize: '1rem',
                              height: 'auto',
                              py: 1
                            }}
                          />
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Engagement Score
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ 
                              flexGrow: 1,
                              height: 8,
                              bgcolor: '#f0f0f0',
                              borderRadius: 1,
                              overflow: 'hidden',
                              mr: 2
                            }}>
                              <Box sx={{ 
                                width: `${(getCurrentQuarterMetrics.engagement_score / 10) * 100}%`,
                                height: '100%',
                                bgcolor: getCurrentQuarterMetrics.engagement_score >= 8 ? '#4caf50' : 
                                         getCurrentQuarterMetrics.engagement_score >= 4 ? '#ff9800' : '#f44336'
                              }} />
                            </Box>
                            <Typography variant="h6">
                              {getCurrentQuarterMetrics.engagement_score || 0}/10
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Compensation Gap
                          </Typography>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              color: getCurrentQuarterMetrics.compensation_gap_percentage < 0 ? '#f44336' : '#4caf50'
                            }}
                          >
                            {getCurrentQuarterMetrics.compensation_gap_percentage || 0}%
                            {getCurrentQuarterMetrics.compensation_gap_percentage < 0 && (
                              <Typography variant="caption" display="block" color="error">
                                Underpaid
                              </Typography>
                            )}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Work-Life Balance
                          </Typography>
                          <Typography variant="h6">
                            {getCurrentQuarterMetrics.work_life_balance_score || 0}/10
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Absentee Rate
                          </Typography>
                          <Typography variant="h6">
                            {getCurrentQuarterMetrics.absenteeism_rate || 0}%
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Current CTC
                          </Typography>
                          <Typography variant="h6">
                            {companyConfig?.currency || '₹'}{getCurrentQuarterMetrics.current_ctc?.toLocaleString() || 'N/A'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Market CTC
                          </Typography>
                          <Typography variant="h6">
                            {companyConfig?.currency || '₹'}{getCurrentQuarterMetrics.market_ctc?.toLocaleString() || 'N/A'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Salary Hike
                          </Typography>
                          <Typography variant="h6">
                            {getCurrentQuarterMetrics.salary_hike || 0}%
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Tenure (Current)
                          </Typography>
                          <Typography variant="h6">
                            {getCurrentQuarterMetrics.tenureinpresent || 0} years
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Total Experience
                          </Typography>
                          <Typography variant="h6">
                            {getCurrentQuarterMetrics.tenure || 0} years
                          </Typography>
                        </Paper>
                      </Grid>
                       <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Shift Type
                          </Typography>
                          <Typography variant="h6">
                            {getCurrentQuarterMetrics.shiftType || "Regular"} 
                          </Typography>
                        </Paper>
                      </Grid>
                       <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Education Level
                          </Typography>
                          <Typography variant="h6">
                            {getCurrentQuarterMetrics.educationLevel || "Bachelor's"}
                          </Typography>
                        </Paper>
                      </Grid>
                       <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Distance from Office 
                          </Typography>
                          <Typography variant="h6">
                            {getCurrentQuarterMetrics.distance_km || 0} km
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Variable Pay vs Expected Pay 
                          </Typography>
                          <Typography variant="h6">
                            {getCurrentQuarterMetrics.variablepay || 0}%
                          </Typography>
                        </Paper>
                      </Grid>
                       <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Last Promotion (24 Months Ago)
                          </Typography>
                          <Typography variant="h6">
                            {getCurrentQuarterMetrics.lastPromotionMonths?"Yes":"No"}
                          </Typography>
                        </Paper>
                      </Grid>

                       <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Internal Mobility History
                          </Typography>
                          <Typography variant="h6">
                            {getCurrentQuarterMetrics.mobility ? "Yes" : "No"} 
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Age
                          </Typography>
                          <Typography variant="h6">
                            {getCurrentQuarterMetrics.age || "N/A"} 
                          </Typography>
                        </Paper>
                      </Grid><Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Job Role
                          </Typography>
                          <Typography variant="h6">
                            {getCurrentQuarterMetrics.JobRole||"N/A"} 
                          </Typography>
                        </Paper>
                      </Grid>
                          <Grid item xs={12} md={2}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Manager Satification Score
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ 
                              flexGrow: 1,
                              height: 8,
                              bgcolor: '#f0f0f0',
                              borderRadius: 1,
                              overflow: 'hidden',
                              mr: 2
                            }}>
                              <Box sx={{ 
                                width: `${(getCurrentQuarterMetrics.engagement_score / 5) * 100}%`,
                                height: '100%',
                                bgcolor: getCurrentQuarterMetrics.engagement_score >= 4 ? '#4caf50' : 
                                         getCurrentQuarterMetrics.engagement_score >= 2 ? '#ff9800' : '#f44336'
                              }} />
                            </Box>
                            <Typography variant="h6">
                              {getCurrentQuarterMetrics.engagement_score || 0}/5
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
          </Grid>
        )}

        {tabValue === 1 && (
          <Grid container spacing={3}>
            {/* Full Width Table */}
            <Grid item xs={12}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    All Employees Latest Quarter Risk Data
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Employee</TableCell>
                          {filters.showJobRole && <TableCell>Designation</TableCell>}
                          {filters.showDepartment && <TableCell>Department</TableCell>}
                          <TableCell>Latest Quarter</TableCell>
                          <TableCell>Risk Level</TableCell>
                          <TableCell>Risk Score</TableCell>
                          {filters.showTenure && <TableCell>Tenure</TableCell>}
                          {filters.showTotalExperience && <TableCell>Total Experience</TableCell>}
                          {filters.showPerformance && <TableCell>Performance Trend</TableCell>}
                          {filters.showEngagement && <TableCell>Engagement Score</TableCell>}
                          {filters.showCompensation && <TableCell>Market Gap</TableCell>}
                          {filters.showWorkLifeBalance && <TableCell>Work-Life Balance</TableCell>}
                          {filters.showAbsenteeism && <TableCell>Absentee Rate</TableCell>}
                          {filters.showAge && <TableCell>Age</TableCell>}
                          {filters.showEducation && <TableCell>Education</TableCell>}
                          {filters.showCurrentCTC && <TableCell>Current CTC</TableCell>}
                          {filters.showMarketCTC && <TableCell>Market CTC</TableCell>}
                          {filters.showSalaryHike && <TableCell>Salary Hike %</TableCell>}
                          {filters.showmanagerSatisfactionScore && <TableCell>Manager Satisfaction Score</TableCell>}
                          {filters.showDistance && <TableCell>Distance to Office(KM)</TableCell>}
                          {filters.showShiftType && <TableCell>Shift Type</TableCell>}
                          {filters.showMobility && <TableCell>Internal Mobility History</TableCell>}
                          {filters.showPromotions && <TableCell>Promotion In last (24 Months)</TableCell>}
                          {filters.showVariablePay && <TableCell>Variable vs Expected Pay</TableCell>}
                          <TableCell>Quarterly Metrics</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {employees
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((employee) => (
                            <TableRow 
                              key={employee.employee_id}
                              hover
                              onClick={() => {
                                setSelectedEmployee(employee);
                                setTabValue(0);
                                fetchEmployeeMonthlyHistory(employee.employee_id);
                              }}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>
                                <Typography variant="subtitle2">
                                  {employee.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {employee.employee_id}
                                </Typography>
                              </TableCell>
                              {filters.showJobRole && (<TableCell>{employee.designation}</TableCell>)}
                              {filters.showDepartment && (
                                <TableCell>{employee.department || 'N/A'}</TableCell>
                              )}
                              <TableCell>
                                <Box>
                                  <Typography variant="subtitle2">
                                    {employee.quarterYear}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={employee.risk_level} 
                                  color={
                                    employee.risk_level === 'High' ? 'error' :
                                    employee.risk_level === 'Medium' ? 'warning' : 'success'
                                  }
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <CircularProgress 
                                    variant="determinate" 
                                    value={employee.risk_score} 
                                    size={24}
                                    thickness={4}
                                    sx={{ 
                                      color: riskColors[employee.risk_level],
                                      mr: 1
                                    }}
                                  />
                                  {employee.risk_score}%
                                </Box>
                              </TableCell>
                              {filters.showTenure && (
                                <TableCell>{employee.tenureinpresent || 0} year</TableCell>
                              )}
                              {filters.showTotalExperience && (
                                <TableCell>{employee.tenure || 0} year</TableCell>
                              )}
                              {filters.showPerformance && (
                                <TableCell>
                                  <Chip 
                                    label={employee.performance_trend || 'No Data'} 
                                    size="small"
                                    sx={{ 
                                      backgroundColor: performanceTrendColors[employee.performance_trend?.toLowerCase()] + '20',
                                      color: performanceTrendColors[employee.performance_trend?.toLowerCase()],
                                      border: `1px solid ${performanceTrendColors[employee.performance_trend?.toLowerCase()]}`
                                    }}
                                  />
                                </TableCell>
                              )}
                              {filters.showEngagement && (
                                <TableCell>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    gap: 1 
                                  }}>
                                    <Box sx={{ 
                                      width: '100%', 
                                      height: 8,
                                      bgcolor: '#f0f0f0',
                                      borderRadius: 1,
                                      overflow: 'hidden'
                                    }}>
                                      <Box sx={{ 
                                        width: `${(employee.engagement_score / 10) * 100}%`,
                                        height: '100%',
                                        bgcolor: employee.engagement_score >= 8 ? '#4caf50' : 
                                                 employee.engagement_score >= 4 ? '#ff9800' : '#f44336'
                                      }} />
                                    </Box>
                                    <Typography variant="body2">
                                      {employee.engagement_score || 0}/10
                                    </Typography>
                                  </Box>
                                </TableCell>
                              )}
                               {filters.showmanagerSatisfactionScore && (
                                <TableCell>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    gap: 1 
                                  }}>
                                    <Box sx={{ 
                                      width: '100%', 
                                      height: 8,
                                      bgcolor: '#f0f0f0',
                                      borderRadius: 1,
                                      overflow: 'hidden'
                                    }}>
                                      <Box sx={{ 
                                        width: `${(employee.engagement_score / 10) * 100}%`,
                                        height: '100%',
                                        bgcolor: employee.engagement_score >= 8 ? '#4caf50' : 
                                                 employee.engagement_score >= 4 ? '#ff9800' : '#f44336'
                                      }} />
                                    </Box>
                                    <Typography variant="body2">
                                      {employee.engagement_score || 0}/10
                                    </Typography>
                                  </Box>
                                </TableCell>
                              )}
                              {filters.showCompensation && (
                                <TableCell>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      color: employee.compensationGapPercentage < 0 ? '#f44336' : '#4caf50'
                                    }}
                                  >
                                    {employee.compensationGapPercentage || 0}%
                                  </Typography>
                                </TableCell>
                              )}
                              {filters.showWorkLifeBalance && (
                                <TableCell>
                                  <Typography variant="body2">
                                    {employee.workLifeBalanceScore || 0}/10
                                  </Typography>
                                </TableCell>
                              )}
                              {filters.showAbsenteeism && (
                                <TableCell>
                                  <Typography variant="body2">
                                    {employee.absenteeismRate || 0}%
                                  </Typography>
                                </TableCell>
                              )}
                              {filters.showAge && (
                                <TableCell>{employee.age || 'N/A'}</TableCell>
                              )}
                              {filters.showEducation && (
                                <TableCell>{employee.educationLevel || 'N/A'}</TableCell>
                              )}
                              {filters.showCurrentCTC && (
                                <TableCell>
                                  {employee.currentCTC ? `${companyConfig?.currency || '₹'}${employee.currentCTC}` : 'N/A'}
                                </TableCell>
                              )}
                              {filters.showMarketCTC && (
                                <TableCell>
                                  {employee.marketCTC ? `${companyConfig?.currency || '₹'}${employee.marketCTC.toLocaleString()}` : 'N/A'}
                                </TableCell>
                              )}
                              {filters.showSalaryHike && (
                                <TableCell>
                                  {employee.salary_hike ? `${employee.salary_hike}%` : 'N/A'}
                                </TableCell>
                              )}
                               {filters.showDistance && (
                                <TableCell>{employee.distanceKm || '0'}Km</TableCell>
                              )}
                                {filters.showShiftType && (
                                <TableCell>{employee.shiftType || 'General Shift'}</TableCell>
                              )}
                              {filters.showMobility && (
                                <TableCell>{employee.internalMobility ? "Yes":"No"}</TableCell>
                              )}
                              {filters.showPromotions && (
                                <TableCell>{employee.promotion_last_24_months ? "Yes":"No"}</TableCell>
                              )}
                            
                             
                              {filters.showVariablePay && (
                                <TableCell>{employee.variablepay || '0%'}</TableCell>
                              )}
                              
                             
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewQuarterlyMetrics(employee);
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={employees.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Pie Chart Below - Full Width */}
            <Grid item xs={12}>
              <Grid container spacing={3}>
                {/* Pie Chart Card */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom align="center">
                        Current Quarter Risk Distribution
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={riskDistributionData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          >
                            {riskDistributionData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => [value, 'Employees']} />
                        </PieChart>
                      </ResponsiveContainer>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                        {riskDistributionData.map((item, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', m: 0.5 }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                backgroundColor: PIE_COLORS[index],
                                mr: 1,
                                borderRadius: 1
                              }}
                            />
                            <Typography variant="body2">
                              {item.name}: {item.value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Risk Level Summary Card */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Risk Level Summary
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
                        {riskDistributionData.map((item, index) => (
                          <Paper
                            key={index}
                            sx={{
                              p: 2,
                              backgroundColor: PIE_COLORS[index] + '20',
                              borderLeft: `4px solid ${PIE_COLORS[index]}`,
                              flex: 1
                            }}
                          >
                            <Typography variant="subtitle1" fontWeight="bold">
                              {item.name}
                            </Typography>
                            <Typography variant="body2">
                              {item.value} employee{item.value !== 1 ? 's' : ''}
                              {employees.length > 0 && ` (${Math.round((item.value / employees.length) * 100)}%)`}
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* Calculate Monthly Attrition Dialog */}
        <Dialog open={calculateDialogOpen} onClose={() => setCalculateDialogOpen(false)} maxWidth="md" fullWidth sx={{padding: 10}}>
          <DialogTitle sx={{alignItems: 'center', display: 'flex', justifyContent: 'center', fontWeight: 'bold'}}>
            Calculate Monthly Attrition Risk
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              This will calculate and store attrition risk for all active employees for:
              <strong>
                {tabValue === 0 
                  ? `${employeeTabState.selectedDate.getMonth() + 1}/${employeeTabState.selectedDate.getFullYear()}`
                  : `${allEmployeesTabState.selectedDate.getMonth() + 1}/${allEmployeesTabState.selectedDate.getFullYear()}`
                }
              </strong>
            </Typography>

            {(tabValue === 0 ? employeeTabState.dataExists : allEmployeesTabState.dataExists) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Data already exists for this period. Calculating again will create duplicate records.
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              This process may take several minutes depending on the number of employees.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCalculateDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={calculateMonthlyAttrition} 
              variant="contained" 
              disabled={calculating || (tabValue === 0 ? employeeTabState.dataExists : allEmployeesTabState.dataExists)}
            >
              {calculating ? <CircularProgress size={24} /> : 'Calculate'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Quarterly Metrics Dialog */}
        <Dialog 
          open={quarterlyMetricsDialogOpen} 
          onClose={() => setQuarterlyMetricsDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Quarterly Metrics - {selectedEmployeeForMetrics?.name}
            <Typography variant="caption" display="block" color="text.secondary">
              Employee ID: {selectedEmployeeForMetrics?.employee_id} | Designation: {selectedEmployeeForMetrics?.designation}
            </Typography>
          </DialogTitle>
          <DialogContent>
            {loadingMetrics ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Quarter</TableCell>
                      <TableCell>Risk Score</TableCell>
                      <TableCell>Risk Level</TableCell>
                      <TableCell>Performance</TableCell>
                      <TableCell>Engagement</TableCell>
                      <TableCell>Comp Gap</TableCell>
                      <TableCell>Work-Life Balance</TableCell>
                      <TableCell>Absent rate </TableCell>
                      <TableCell>Current CTC</TableCell>
                      <TableCell>Market CTC</TableCell>
                      <TableCell>Salary Hike</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employeeQuarterlyMetrics.map((quarterData, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography fontWeight="medium">
                            {quarterData.quarter} {quarterData.year}
                          </Typography>
                          {!quarterData.hasData && (
                            <Typography variant="caption" color="text.secondary">
                              No data
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {quarterData.hasData ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <CircularProgress 
                                variant="determinate" 
                                value={quarterData.risk_score} 
                                size={24}
                                sx={{ 
                                  color: riskColors[quarterData.risk_level],
                                  mr: 1
                                }}
                              />
                              {Math.round(quarterData.risk_score)}%
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {quarterData.hasData ? (
                            <Chip 
                              label={quarterData.risk_level}
                              size="small"
                              color={
                                quarterData.risk_level === 'High' ? 'error' :
                                quarterData.risk_level === 'Medium' ? 'warning' : 'success'
                              }
                            />
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {quarterData.hasData ? (
                            <Chip 
                              label={quarterData.performance_trend}
                              size="small"
                              sx={{ 
                                backgroundColor: performanceTrendColors[quarterData.performance_trend?.toLowerCase()] + '20',
                                color: performanceTrendColors[quarterData.performance_trend?.toLowerCase()],
                                border: `1px solid ${performanceTrendColors[quarterData.performance_trend?.toLowerCase()]}`
                              }}
                            />
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {quarterData.hasData ? `${quarterData.engagement_score}/10` : '-'}
                        </TableCell>
                        <TableCell>
                          {quarterData.hasData ? `${quarterData.compensation_gap_percentage}%` : '-'}
                        </TableCell>
                        <TableCell>
                          {quarterData.hasData ? `${quarterData.work_life_balance_score}/10` : '-'}
                        </TableCell>
                        <TableCell>
                          {quarterData.hasData ? `${quarterData.absenteeism_rate}%` : '-'}
                        </TableCell>
                        <TableCell>
                          {quarterData.hasData ? `${companyConfig?.currency || '₹'}${quarterData.current_ctc?.toLocaleString() || 0}` : '-'}
                        </TableCell>
                        <TableCell>
                          {quarterData.hasData ? `${companyConfig?.currency || '₹'}${quarterData.market_ctc?.toLocaleString() || 0}` : '-'}
                        </TableCell>
                        <TableCell>
                          {quarterData.hasData ? `${quarterData.salary_hike}%` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setQuarterlyMetricsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Filter Dialog */}
        <FilterDialog />

        <Snackbar 
          open={alert.open} 
          autoHideDuration={6000} 
          onClose={() => setAlert(prev => ({ ...prev, open: false }))} 
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert 
            onClose={() => setAlert(prev => ({ ...prev, open: false }))} 
            severity={alert.severity}
            sx={{ width: '100%', boxShadow: 3 }}
          >
            {alert.message}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
};

export default MonthlyAttritionDashboard;